// src/pages/products/ProductDetailPage/ProductDetailPage.jsx
import React, { useCallback, useEffect, useRef, useState } from "react";
import { useHistory, useParams } from "react-router-dom";
import ProductsAPI from "../../../api/v3/products-api-v3";
import ProductImagesAPI from "../../../api/v3/product-images-api-v3";
import AsyncLookup from "../../../components/form/AsyncLookup";

import ManualDeleteButton, { MANUAL_DELETE_NOTE } from "../../../components/bagspa/ManualDeleteButton";

/* ------------------------------------------
   Constants
--------------------------------------------- */
const KINDS = [
  "front_view",
  "right_side_view",
  "rear_view",
  "left_side_view",
  "top_view",
  "bottom_view",
  "comes_with_image",
  "stamp_image",
  "photo_details",
];

const API_BASE = process.env.REACT_APP_API_BASE;

function fmtBytes(n) {
  if (!n && n !== 0) return "-";
  const u = ["B", "KB", "MB", "GB"];
  let i = 0, v = n;
  while (v >= 1024 && i < u.length - 1) { v /= 1024; i++; }
  return `${v.toFixed(1)} ${u[i]}`;
}

// ---- size helpers ----
function cmLabelFor(kind, min_cm, max_cm) {
  if (kind !== 'bag') return '' // only bags use numeric cm banding here
  if (min_cm && max_cm) return `${min_cm}-${max_cm} cm`
  if (min_cm && !max_cm) return `≥${min_cm} cm`
  if (!min_cm && max_cm) return `≤${max_cm} cm`
  return ''
}

/** Build a compact label for a Size record */
function buildSizeOptionLabel(s) {
  const cm = cmLabelFor(s.kind, s.min_cm, s.max_cm)
  const bits = [
    cm || null,
    s.kind && s.kind !== 'generic' ? s.kind : null,
    s.dimension_text || null,
  ].filter(Boolean)
  // Example: "M · 26–29 cm • bag • Bag: dominant side 26–29 cm"
  const right = bits.length ? ` · ${bits.join(' • ')}` : ''
  return `${s.code || s.name || s.id}${right}`
}

/** Build a readable size display for read-only section */
function renderSizeReadOnly(p) {
  const main =
    p.size_label ||
    p.size_name ||
    p.size_code ||
    "-";

  const parts = [];
  if (p.size_kind) parts.push(`Kind: ${p.size_kind}`);
  if (typeof p.size_min_cm !== "undefined" && p.size_min_cm !== null)
    parts.push(`Min: ${p.size_min_cm}cm`);
  if (typeof p.size_max_cm !== "undefined" && p.size_max_cm !== null)
    parts.push(`Max: ${p.size_max_cm}cm`);
  if (p.size_dimension) parts.push(p.size_dimension);

  return (
    <>
      <input className="form-control" value={main} disabled />
      {parts.length > 0 && (
        <small className="text-muted d-block mt-1">
          {parts.join(" • ")}
        </small>
      )}
    </>
  );
}

/* ------------------------------------------
   Main Component
--------------------------------------------- */
export default function ProductDetailPage() {
  const { id } = useParams();
  const history = useHistory();
  const [product, setProduct] = useState(null);
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);

  // Taxonomy fields
  const [customer, setCustomer] = useState(null);
  const [brand, setBrand] = useState(null);
  const [category, setCategory] = useState(null);
  const [colour, setColour] = useState(null);
  const [size, setSize] = useState(null);
  const [material, setMaterial] = useState(null);

  // Images
  const [rows, setRows] = useState([]);
  const [files, setFiles] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [savingMap, setSavingMap] = useState(false);
  const fileInputRef = useRef(null);

  /* ------------------------------------------
     Load Product & Images
  --------------------------------------------- */
  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  async function load() {
    try {
      const res = await ProductsAPI.show(id);
      const item = res?.item || res?.data || res;
      setProduct(item);

      // Sync taxonomy objects (with richer Size mapping)
      setCustomer(
        item.customer_id
          ? { id: item.customer_id, name: item.customer_name, email: item.customer_email }
          : null
      );
      setBrand(item.brand_id ? { id: item.brand_id, name: item.brand_name } : null);
      setCategory(item.category_id ? { id: item.category_id, name: item.category_name } : null);
      setColour(item.colour_id ? { id: item.colour_id, name: item.colour_name } : null);

      setSize(item.size_id
        ? {
            id: item.size_id,
            name: item.size_name,
            label: item.size_label || item.size_name || item.size_code,
            code: item.size_code,
            kind: item.size_kind,
            min_cm: item.size_min_cm,
            max_cm: item.size_max_cm,
            dimension_text: item.size_dimension,
          }
        : null
      );

      setMaterial(item.material_id ? { id: item.material_id, name: item.material_name } : null);

      const imgs = item.product_images || [];
      const mapped = imgs.map((x, idx) => ({
        id: x.id,
        url: x.url,
        kind: x.kind || "",
        is_cover: !!x.is_cover,
        position: x.position ?? idx,
        byte_size: x.byte_size,
        created_at: x.created_at,
      }));
      setRows(mapped.sort((a, b) => (a.position || 0) - (b.position || 0)));
    } catch (e) {
      setError(e.message);
    }
  }

  /* ------------------------------------------
     Fetch dropdowns
  --------------------------------------------- */
  const unwrapItems = (payload) => {
    if (!payload) return [];
    if (Array.isArray(payload)) return payload;
    if (Array.isArray(payload.items)) return payload.items;
    if (Array.isArray(payload.data)) return payload.data;
    return [];
  };

  const fetchSimpleList = useCallback(async (path, q) => {
    const url = new URL(`${API_BASE}${path}`);
    if (q) url.searchParams.set("q", q);
    url.searchParams.set("per", "20");
    const res = await fetch(url.toString(), {
      credentials: "include",
      headers: { Accept: "application/json" },
    });
    const json = await res.json();
    return unwrapItems(json).map((x) => ({
      id: x.id,
      label: x.name || String(x.id),
      name: x.name,
    }));
  }, []);

  const fetchCustomers = useCallback((q) => fetchSimpleList("/api/v3/customers", q), [fetchSimpleList]);
  const fetchBrands    = useCallback((q) => fetchSimpleList("/api/v3/brands", q), [fetchSimpleList]);
  const fetchCategories= useCallback((q) => fetchSimpleList("/api/v3/categories", q), [fetchSimpleList]);
  const fetchColours   = useCallback((q) => fetchSimpleList("/api/v3/colours", q), [fetchSimpleList]);
  const fetchMaterials = useCallback((q) => fetchSimpleList("/api/v3/materials", q), [fetchSimpleList]);

  // Manual sizes fetch — we need all fields for nice label and editing
  const fetchSizes = useCallback(async (q) => {
    const url = new URL(`${API_BASE}/api/v3/sizes`);
    if (q) url.searchParams.set("q", q);
    url.searchParams.set("per", "20");

    const res = await fetch(url.toString(), {
      credentials: "include",
      headers: { Accept: "application/json" },
    });
    const json = await res.json();
    const items = unwrapItems(json);

    return items.map((s) => ({
      id: s.id,
      label: buildSizeOptionLabel(s),
      name: s.name,
      code: s.code,
      kind: s.kind,
      min_cm: s.min_cm,
      max_cm: s.max_cm,
      dimension_text: s.dimension_text,
    }));
  }, []);

  /* ------------------------------------------
     Manual Save Handler
  --------------------------------------------- */
  async function onSave() {
    try {
      setSaving(true);
      const payload = {
        product: {
          customer_id: customer?.id || null,
          brand_id:    brand?.id || null,
          category_id: category?.id || null,
          colour_id:   colour?.id || null,
          size_id:     size?.id || null,
          material_id: material?.id || null,
        },
      };
      await ProductsAPI.update(product.id, payload);
      await load();
      alert("✅ Product details updated successfully");
    } catch (e) {
      setError(e.message);
    } finally {
      setSaving(false);
    }
  }

  /* ------------------------------------------
     Image Upload + Mapping
  --------------------------------------------- */
  const onPick = () => fileInputRef.current?.click();
  const onFiles = (list) => {
    const arr = Array.from(list || []).filter((f) => /^image\//.test(f.type));
    if (arr.length) setFiles((prev) => [...prev, ...arr]);
  };
  const onDrop = (e) => {
    e.preventDefault();
    onFiles(e.dataTransfer?.files);
  };
  const onDragOver = (e) => e.preventDefault();

  async function uploadAll() {
    if (!files.length) {
      alert("Select images first.");
      return;
    }
    try {
      setUploading(true);
      await ProductImagesAPI.bulkUpload(product.id, files);
      setFiles([]);
      await load();
    } catch (e) {
      setError(e.message);
    } finally {
      setUploading(false);
    }
  }

  const setRow = (id, patch) => setRows((prev) => prev.map((r) => (r.id === id ? { ...r, ...patch } : r)));
  const onSetCover = (id) => setRows((prev) => prev.map((r) => ({ ...r, is_cover: r.id === id })));

  async function saveMappings() {
    try {
      setSavingMap(true);
      const payload = rows.map((r) => ({
        id: r.id,
        kind: r.kind || null,
        position: Number.isFinite(+r.position) ? +r.position : 0,
        is_cover: !!r.is_cover,
      }));
      await ProductImagesAPI.updateMany(product.id, payload);
      await load();
      alert("✅ Image mappings updated.");
    } catch (e) {
      setError(e.message);
    } finally {
      setSavingMap(false);
    }
  }

  async function onDeleteImage(id) {
    if (!window.confirm("Delete this image?")) return;
    try {
      await ProductImagesAPI.destroyById(id);
      setRows((prev) => prev.filter((r) => r.id !== id));
    } catch (e) {
      setError(e.message);
    }
  }

  if (!product) return <div>{error ? <div className="alert alert-danger">{error}</div> : 'Loading…'}</div>;

  /* ------------------------------------------
     Render
  --------------------------------------------- */
  return (
    <div>
      <div className="d-flex align-items-center justify-content-between mb-3">
        <h2 className="mb-0">Product #{product.id}</h2>
        {saving && <small className="text-muted">💾 Saving…</small>}
      </div>

      {error && <div className="alert alert-danger">{error}</div>}

      <div className="mb-3">
        <ManualDeleteButton kind="Produk" label={product.sku || `#${id}`} onDelete={() => ProductsAPI.destroy(id)} onDeleted={() => history.replace('/app/sales/bagspa/products')} />
        <p className="text-muted small mt-2">{MANUAL_DELETE_NOTE}</p>
      </div>

      <div className="alert alert-info">
        Form bagian atas hanya menampilkan informasi produk. Untuk mengubah data, gunakan form "Edit Product Details" di bagian bawah, lalu klik "Save Changes".
      </div>

      {/* ----------- 1️⃣ READ-ONLY FORM ----------- */}
      <div className="card mb-4">
        <div className="card-body">
          <h6 className="text-muted mb-3">Product Info (Read Only)</h6>

          <div className="form-row">
            <div className="form-group col-md-6">
              <label>SKU</label>
              <input className="form-control" value={product.sku || ""} disabled />
            </div>
            <div className="form-group col-md-6">
              <label>Customer</label>
              <input className="form-control" value={product.customer_name || "-"} disabled />
            </div>
          </div>

          <div className="form-row">
            <div className="form-group col-md-6">
              <label>Brand</label>
              <input className="form-control" value={product.brand_name || "-"} disabled />
            </div>
            <div className="form-group col-md-6">
              <label>Category</label>
              <input className="form-control" value={product.category_name || "-"} disabled />
            </div>
          </div>

          <div className="form-row">
            <div className="form-group col-md-6">
              <label>Colour</label>
              <input className="form-control" value={product.colour_name || "-"} disabled />
            </div>
            <div className="form-group col-md-6">
              <label>Size</label>
              {renderSizeReadOnly(product)}
            </div>
          </div>

          <div className="form-group">
            <label>Material</label>
            <input className="form-control" value={product.material_name || "-"} disabled />
          </div>
        </div>
      </div>

      {/* ----------- 2️⃣ EDITABLE FORM ----------- */}
      <div className="card mb-4 border-primary">
        <div className="card-body">
          <h6 className="text-primary mb-3">Edit Product Details</h6>

          <div className="form-row">
            <div className="form-group col-md-6">
              <label>Customer <small className="text-muted">(type to search)</small></label>
              <AsyncLookup
                value={customer}
                fetchFn={fetchCustomers}
                onChange={(v) => setCustomer(v)}
                placeholder="Search customer…"
              />
            </div>
            <div className="form-group col-md-6">
              <label>Brand <small className="text-muted">(type to search)</small></label>
              <AsyncLookup
                value={brand}
                fetchFn={fetchBrands}
                onChange={(v) => setBrand(v)}
                placeholder="Search brand…"
              />
            </div>
          </div>

          <div className="form-row">
            <div className="form-group col-md-6">
              <label>Category <small className="text-muted">(type to search)</small></label>
              <AsyncLookup
                value={category}
                fetchFn={fetchCategories}
                onChange={(v) => setCategory(v)}
                placeholder="Search category…"
              />
            </div>
            <div className="form-group col-md-6">
              <label>Colour <small className="text-muted">(type to search)</small></label>
              <AsyncLookup
                value={colour}
                fetchFn={fetchColours}
                onChange={(v) => setColour(v)}
                placeholder="Search colour…"
              />
            </div>
          </div>

          <div className="form-row">
            <div className="form-group col-md-6">
              <label>Size <small className="text-muted">(type to search)</small></label>
              <AsyncLookup
                value={size}
                fetchFn={fetchSizes}
                onChange={(v) => setSize(v)}
                placeholder="Search size…"
              />
            </div>
            <div className="form-group col-md-6">
              <label>Material <small className="text-muted">(type to search)</small></label>
              <AsyncLookup
                value={material}
                fetchFn={fetchMaterials}
                onChange={(v) => setMaterial(v)}
                placeholder="Search material…"
              />
            </div>
          </div>

          <div className="text-right mt-3">
            <button className="btn btn-primary" disabled={saving} onClick={onSave}>
              {saving ? "Saving…" : "Save Changes"}
            </button>
          </div>
        </div>
      </div>

      {/* -------- Images Section -------- */}
      <h5>Images</h5>
      <p className="text-muted">
        Drag & drop or click to upload images. Then assign kind, position, and cover below.
      </p>

      <div
        onDrop={onDrop}
        onDragOver={onDragOver}
        className="mb-3 p-4 border rounded text-center"
        style={{ background: "#fafafa" }}
      >
        <div className="mb-2">Drop files here</div>
        <button className="btn btn-sm btn-primary" onClick={onPick}>
          Choose Files
        </button>
        <input
          ref={fileInputRef}
          type="file"
          multiple
          accept="image/*"
          className="d-none"
          onChange={(e) => onFiles(e.target.files)}
        />
        {files.length > 0 && (
          <div className="mt-3 small text-muted">
            {files.length} file(s) selected
            <div className="mt-2 d-flex flex-wrap gap-2">
              {files.map((f, i) => (
                <span key={i} className="badge text-bg-light">{f.name}</span>
              ))}
            </div>
          </div>
        )}
        <div className="mt-3">
          <button
            className="btn btn-success"
            disabled={uploading || files.length === 0}
            onClick={uploadAll}
          >
            {uploading ? "Uploading…" : "Upload"}
          </button>
        </div>
      </div>

      {/* -------- Image Table -------- */}
      <div className="card mb-5">
        <div className="card-body">
          <div className="d-flex justify-content-between align-items-center mb-2">
            <h6 className="mb-0">Assign Kinds & Cover</h6>
            <button className="btn btn-primary btn-sm" disabled={savingMap} onClick={saveMappings}>
              {savingMap ? "Saving…" : "Save Assignments"}
            </button>
          </div>

          {rows.length ? (
            <div className="table-responsive">
              <table className="table table-sm align-middle">
                <thead className="table-light">
                  <tr>
                    <th style={{ width: 72 }}>Thumb</th>
                    <th>URL</th>
                    <th style={{ width: 200 }}>Kind</th>
                    <th style={{ width: 100 }}>Position</th>
                    <th style={{ width: 80 }}>Cover</th>
                    <th style={{ width: 100 }}>Size</th>
                    <th style={{ width: 180 }}>Created</th>
                    <th style={{ width: 60 }} />
                  </tr>
                </thead>
                <tbody>
                  {rows.map((r) => (
                    <tr key={r.id}>
                      <td>
                        <div
                          style={{
                            width: 60,
                            height: 60,
                            overflow: "hidden",
                            borderRadius: 6,
                            border: "1px solid #eee",
                          }}
                        >
                          <img
                            src={r.url}
                            alt=""
                            style={{ width: "100%", height: "100%", objectFit: "cover" }}
                          />
                        </div>
                      </td>
                      <td className="small text-break">
                        <a href={r.url} target="_blank" rel="noreferrer">{r.url}</a>
                      </td>
                      <td>
                        <select
                          className="form-select form-select-sm"
                          value={r.kind}
                          onChange={(e) => setRow(r.id, { kind: e.target.value })}
                        >
                          <option value="">— choose kind —</option>
                          {KINDS.map((k) => (
                            <option key={k} value={k}>{k}</option>
                          ))}
                        </select>
                      </td>
                      <td>
                        <input
                          type="number"
                          className="form-control form-control-sm"
                          value={r.position}
                          onChange={(e) => setRow(r.id, { position: e.target.value })}
                        />
                      </td>
                      <td className="text-center">
                        <input
                          type="radio"
                          name="cover"
                          checked={!!r.is_cover}
                          onChange={() => onSetCover(r.id)}
                        />
                      </td>
                      <td className="small">{fmtBytes(r.byte_size)}</td>
                      <td className="small">
                        {r.created_at ? new Date(r.created_at).toLocaleString("id-ID") : "-"}
                      </td>
                      <td className="text-center">
                        <button
                          className="btn btn-sm btn-outline-danger"
                          onClick={() => onDeleteImage(r.id)}
                        >
                          <i className="fa fa-trash" />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="text-muted">No images yet.</div>
          )}
        </div>
      </div>
    </div>
  );
}
